import {
  Button,
  Divider,
  Form,
  FormInstance,
  Input,
  InputNumber,
  Space,
  Switch,
  Tooltip,
} from "antd";
import Select, { BaseOptionType } from "antd/lib/select";
import { isEmailValid } from "../../../../../utils/isEmail";
import {
  BusinessSettingsEndOfShiftCalculationType,
  IBusinessFull,
  IPosRTConfig,
  ScheduleType,
} from "../../../../../interfaces/businessFull.interface";
import { Language } from "../../../../../interfaces/language.enum";
import { useEffect, useState } from "react";
import {
  PlusCircleOutlined,
  CloseCircleOutlined,
  InfoCircleOutlined,
} from "@ant-design/icons";
import InsightsResyncButton from "../../../../insights-admin/components/InsightsResyncButton";
const handleSendListValidation = (emails: string[]) => {
  let illegalEmail = "";
  emails.forEach((e) => {
    if (!isEmailValid(e)) illegalEmail = e;
  });
  return illegalEmail;
};
const settingsFieldLayout = {
  labelCol: { span: 7 },
  wrapperCol: { span: 12 },
};
const inputFieldLayout = {
  labelCol: { span: 12 },
  wrapperCol: { span: 7 },
};
const checkboxFieldLayout = {
  labelCol: { span: 20 },
  wrapperCol: { span: 20 },
};
const shortNumberFieldLayout = {
  labelCol: { span: 18 },
  wrapperCol: { span: 12 },
};
const shortNumberFieldWithSwitchLayout = {
  labelCol: { span: 18 },
  wrapperCol: { span: 20 },
};
const idleScreenLanguages: { code: Language; label: string }[] = [
  { code: Language.English, label: "English" },
  { code: Language.Dutch, label: "Dutch" },
  { code: Language.French, label: "French" },
  { code: Language.German, label: "German" },
  { code: Language.Italian, label: "Italian" },
];
const idleScreenLogoutTexts: {
  key:
    "logoutButtonText" | "logoutPopconfirmTitle" | "logoutPopconfirmSubtitle";
  label: string;
  multiline?: boolean;
}[] = [
  { key: "logoutButtonText", label: "Logout Button Text" },
  { key: "logoutPopconfirmTitle", label: "Logout Confirmation Title" },
  {
    key: "logoutPopconfirmSubtitle",
    label: "Logout Confirmation Subtitle",
    multiline: true,
  },
];
const hexColorPattern = /^#([0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/;
export default function SettingsEditor({
  currentBusiness,
  posRTConfigs,
  form,
}: {
  currentBusiness: IBusinessFull;
  form: FormInstance<any>;
  posRTConfigs: IPosRTConfig[];
}) {
  const [showTipOptions, setShowTipOptions] = useState(
    currentBusiness?.settings?.payments?.enableTips
  );
  const [showDepositOptions, setShowDepositOptions] = useState(
    currentBusiness?.settings?.payments?.isDepositEnabled
  );
  const [showCustomTipButtonTextField, setShowCustomTipButtonTextField] =
    useState(
      currentBusiness?.settings?.payments?.enableTips &&
        !currentBusiness?.settings?.payments?.customTipButtonDisabled
    );
  const [showThirdTipOption, setShowThirdTipOption] = useState(
    currentBusiness?.settings?.payments?.tipPercentagePresets?.hidePercentage3
      ? false
      : true
  );

  const [
    showSalesOverviewScheduleTypeOptions,
    setShowSalesOverviewScheduleTypeOptions,
  ] = useState(
    currentBusiness?.settings?.reports?.types?.endOfDaySalesOverviewReport
      ?.isEnabled
  );
  const [showEndOfShiftPreferences, setShowEndOfShiftPreferences] = useState(
    currentBusiness?.settings?.endOfShift?.enableEndOfShift
  );
  const [showEndOfShiftCode, setShowEndOfShiftCode] = useState(
    currentBusiness?.settings?.endOfShift?.requireEndOfShiftCode
  );
  const [showReviewsOptions, setShowReviewsOptions] = useState(
    currentBusiness?.settings?.reviews?.enableReviews
  );
  const [showReviewsFeatureOptions, setShowReviewsFeatureOptions] = useState(
    currentBusiness?.settings?.reviews?.isReviewsFeatureEnabled
  );
  const [showOnlinePaymentsOptions, setShowOnlinePaymentsOptions] = useState(
    currentBusiness?.settings?.payments?.isOnlinePaymentsFeatureEnabled
  );
  // The logout copy only reaches the terminal when the button is shown, so the
  // fifteen translation fields stay out of the way until it is.
  const [showIdleScreenLogoutTexts, setShowIdleScreenLogoutTexts] = useState(
    currentBusiness?.settings?.idleScreen?.showLogoutButton
  );
  const onTipEnablmentChange = (value: any) => {
    setShowTipOptions(value);
  };
  const onDepositEnablmentChange = (value: any) => {
    setShowDepositOptions(value);
  };
  const onCustomTipButtonDisableChange = (value: any) => {
    setShowCustomTipButtonTextField(!value);
  };

  const onSalesOverviewEnablmentChange = (value: any) => {
    setShowSalesOverviewScheduleTypeOptions(value);
  };
  const onEnableEndOfShiftChange = (value: any) => {
    setShowEndOfShiftPreferences(value);
  };
  const onRequireEndOfShiftCodeChange = (value: any) => {
    setShowEndOfShiftCode(value);
  };
  const onReviewsEnablmentChange = (value: any) => {
    setShowReviewsOptions(value);
  };
  const onReviewsFeatureEnablmentChange = (value: any) => {
    setShowReviewsFeatureOptions(value);
  };
  const onOnlinePaymentsEnablmentChange = (value: any) => {
    setShowOnlinePaymentsOptions(value);
    if (!value) {
      form.setFieldValue(
        ["settings", "payments", "isPayByQRFeatureEnabled"],
        false
      );
      form.setFieldValue(
        ["settings", "payments", "isPayAtTheTableFeatureEnabled"],
        false
      );
    }
  };

  useEffect(() => {
    setShowSalesOverviewScheduleTypeOptions(
      currentBusiness?.settings?.reports?.types?.endOfDaySalesOverviewReport
        ?.isEnabled
    );
    setShowTipOptions(currentBusiness?.settings?.payments?.enableTips);
    setShowReviewsOptions(currentBusiness?.settings?.reviews?.enableReviews);
    setShowReviewsFeatureOptions(
      currentBusiness?.settings?.reviews?.isReviewsFeatureEnabled
    );
    setShowOnlinePaymentsOptions(
      currentBusiness?.settings?.payments?.isOnlinePaymentsFeatureEnabled
    );
    setShowIdleScreenLogoutTexts(
      currentBusiness?.settings?.idleScreen?.showLogoutButton
    );
  }, [
    currentBusiness._id,
    currentBusiness?.settings?.reports?.types?.endOfDaySalesOverviewReport
      ?.isEnabled,
    currentBusiness?.settings?.payments?.enableTips,
    currentBusiness?.settings?.payments?.customTipButtonDisabled,
    currentBusiness?.settings?.reviews?.enableReviews,
    currentBusiness?.settings?.reviews?.isReviewsFeatureEnabled,
    currentBusiness?.settings?.payments?.isOnlinePaymentsFeatureEnabled,
    currentBusiness?.settings?.idleScreen?.showLogoutButton,
  ]);

  return (
    <>
      <h3>Fiscalization</h3>
      <Form.Item
        label="POS RT Config"
        name={["settings", "fiscalization", "posRTConfig"]}
        labelCol={{ ...settingsFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Select
          style={{ width: 300 }}
          placeholder="Select fiscalization configuration"
          key={"selectFiscalizationConfig"}
          options={[
            {
              label: "Remove fiscalization config",
              value: "__none__",
              key: "__none__",
            },
            ...posRTConfigs.map((config) => ({
              label: config.country,
              value: config._id,
              key: config._id,
            })),
          ]}
          allowClear
        />
      </Form.Item>
      <Divider plain></Divider>
      <h3>Reports</h3>
      <Form.Item
        label="Email List"
        name={["settings", "reports", "sendList"]}
        rules={[
          {
            validator: async (_, persons) => {
              if (!persons || persons.length < 1) return;
              const illegalValue = handleSendListValidation(persons);

              if (illegalValue)
                return Promise.reject(
                  new Error(
                    `"Send List contains a non email value ${illegalValue} `
                  )
                );
            },
          },
        ]}
      >
        <Select
          mode="tags"
          style={{ width: 300 }}
          placeholder="Sender List"
          key={"selectEmails"}
          // onChange={handleChange}
          options={currentBusiness?.settings?.reports?.sendList?.map(
            (email, i) => ({
              label: email,
              value: email,
              key: `${email} ${i}`,
            })
          )}
        />
      </Form.Item>
      <Divider plain>Automated Report List</Divider>
      <Form.Item
        name={[
          "settings",
          "reports",
          "types",
          "endOfDaySalesOverviewReport",
          "isEnabled",
        ]}
        label={"Daily Sales Overview Report"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.reports?.types
              ?.endOfDaySalesOverviewReport?.isEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={onSalesOverviewEnablmentChange}
        />
      </Form.Item>
      {showSalesOverviewScheduleTypeOptions && (
        <Form.Item
          name={[
            "settings",
            "reports",
            "types",
            "endOfDaySalesOverviewReport",
            "scheduleType",
          ]}
          label={"Schedule Type"}
          labelCol={{ ...settingsFieldLayout.labelCol }}
          labelAlign="left"
        >
          <Select
            style={{ width: 270 }}
            placeholder="When should the report be sent"
            key={"selectDailySalesOverviewScheduleType"}
            defaultValue={"daily-end-of-day"}
            // onChange={handleChange}
            options={[
              {
                label: `End of day (cutoff time)`,
                value: "daily-end-of-day" as ScheduleType,
                key: "1",
              },
              {
                label: "Day after (09:30 Dutch Time)",
                value: "daily-day-after" as ScheduleType,
                key: "2",
              },
            ]}
          />
        </Form.Item>
      )}
      <Form.Item
        name={[
          "settings",
          "reports",
          "types",
          "dailyPayoutReport",
          "isEnabled",
        ]}
        label={"Daily Payout Report"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.reports?.types?.dailyPayoutReport
              ?.isEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={[
          "settings",
          "reports",
          "types",
          "monthlyAccountingReport",
          "isEnabled",
        ]}
        label={"Monthly Bookkeeping Report"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.reports?.types?.monthlyAccountingReport
              ?.isEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "reports", "isTagAnalyticsReportsEnabled"]}
        label={"Tag Analytics Reports"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.reports?.isTagAnalyticsReportsEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Divider plain></Divider>
      <h3>Cutoff Time</h3>
      <Form.Item
        label="Cutoff Hour"
        name={["settings", "endOfDay", "hour"]}
        labelCol={{ ...shortNumberFieldLayout.labelCol }}
        labelAlign="left"
      >
        <InputNumber
          // min={0}
          // max={12}
          formatter={(value) => `${value}:00`}
          parser={(value) => value!.replace(":00", "")}
        ></InputNumber>
      </Form.Item>
      <Divider plain></Divider>
      <h3>End of Shift</h3>
      <Form.Item
        name={["settings", "endOfShift", "enableEndOfShift"]}
        label={"Enable End of Shift"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.endOfShift?.enableEndOfShift
          }
          onChange={onEnableEndOfShiftChange}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      {showEndOfShiftPreferences && (
        <>
          <Form.Item
            name={["settings", "endOfShift", "calculationType"]}
            label={"Calculation Type"}
            labelCol={{ ...inputFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Select
              style={{ width: "100%" }}
              placeholder="Calculation Type"
              key={"Calculation Type"}
              // onChange={handleChange}
              options={[
                {
                  label: "All devices",
                  value: BusinessSettingsEndOfShiftCalculationType.PerBusiness,
                  key: `All Devices`,
                },
                {
                  label: "Per device",
                  value: BusinessSettingsEndOfShiftCalculationType.PerDevice,
                  key: `Per Device`,
                },
              ]}
            />
          </Form.Item>
          <Form.Item
            name={["settings", "endOfShift", "calculateVolumeFromLastShift"]}
            label={"Reset Shift Volume "}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.endOfShift
                  ?.calculateVolumeFromLastShift
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
          <Form.Item
            name={["settings", "endOfShift", "requireEndOfShiftCode"]}
            label={"Require Shift Code"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.endOfShift?.requireEndOfShiftCode
              }
              onChange={onRequireEndOfShiftCodeChange}
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>

          {showEndOfShiftCode && (
            <Form.Item
              name={["settings", "endOfShift", "endOfShiftCode"]}
              label={"End of Shift Code"}
              labelCol={{ ...shortNumberFieldLayout.labelCol }}
              labelAlign="left"
            >
              <Input minLength={0} maxLength={4}></Input>
            </Form.Item>
          )}

          <Form.Item
            labelCol={{ ...inputFieldLayout.labelCol }}
            name={["settings", "endOfShift", "shiftReportDefaultEmail"]}
            label={"Default Shift Report Email"}
            labelAlign="left"
          >
            <Input max={100}></Input>
          </Form.Item>
        </>
      )}
      <Divider plain></Divider>
      <h3>Payment Flow</h3>
      <Form.Item
        name={["settings", "payments", "enableCashPayments"]}
        label={"Enable Cash Payments"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.payments?.enableCashPayments
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "payments", "enableDiscounts"]}
        label={"Enable Cash Discounts"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={currentBusiness?.settings?.payments?.enableDiscounts}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>

      <h4>Deposits</h4>
      <Form.Item
        name={["settings", "payments", "isDepositFeatureEnabled"]}
        label={"Enable Deposit Feature"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.payments?.isDepositFeatureEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={onDepositEnablmentChange}
        />
      </Form.Item>
      {showDepositOptions && (
        <>
          <Form.Item
            name={["settings", "payments", "isDepositEnabled"]}
            label={"Enable Deposits"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.payments?.isDepositEnabled
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
          {/* <Form.Item
            name={["settings", "payments", "maxDepositDailySum"]}
            label={"Max deposit daily sum"}
            labelCol={{ ...shortNumberFieldLayout.labelCol }}
            labelAlign="left"
            rules={[{ min: 0, type: "number" }]}
            required
          >
            <InputNumber
              controls={false}
              // min={0}
              // max={100000}
              addonBefore={"€"}
            ></InputNumber>
          </Form.Item>
          <Form.Item
            name={["settings", "payments", "maxDepositAmount"]}
            label={"Max deposit amount"}
            labelCol={{ ...shortNumberFieldLayout.labelCol }}
            labelAlign="left"
            rules={[{ min: 0, type: "number" }]}
            required
          >
            <InputNumber
              controls={false}
              // min={0}
              // max={100}
              addonBefore={"€"}
            ></InputNumber>
          </Form.Item> */}
        </>
      )}
      <h4>Tipping</h4>
      <Form.Item
        name={["settings", "payments", "enableTips"]}
        label={"Enable Tips"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={currentBusiness?.settings?.payments?.enableTips}
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={onTipEnablmentChange}
        />
      </Form.Item>
      {showTipOptions && (
        <>
          <Form.Item
            name={["settings", "payments", "customTipButtonDisabled"]}
            label={"Custom Tip Button Disabled"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.payments?.customTipButtonDisabled
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={onCustomTipButtonDisableChange}
            />
          </Form.Item>

          {showCustomTipButtonTextField && (
            <Form.Item
              name={["settings", "payments", "customTipButtonType"]}
              label={"Custom Tip Button Text"}
              labelCol={{ ...inputFieldLayout.labelCol }}
              labelAlign="left"
            >
              <Select style={{ width: "100%" }} placeholder="Custom Tip Button">
                <Select.Option value="customTip">Custom Tip</Select.Option>
                <Select.Option value="roundUp">Round Up</Select.Option>
              </Select>
            </Form.Item>
          )}

          <Form.Item
            name={[
              "settings",
              "payments",
              "tipPercentagePresets",
              "showCalculatedTip",
            ]}
            label={"Show Calculated Tip"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.payments?.tipPercentagePresets
                  .showCalculatedTip
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>

          <Form.Item
            name={[
              "settings",
              "payments",
              "tipPercentagePresets",
              "percentage1",
            ]}
            label={"Tip Option 1"}
            labelCol={{ ...shortNumberFieldLayout.labelCol }}
            labelAlign="left"
          >
            <InputNumber
              controls={false}
              min={0}
              max={100}
              addonAfter={"%"}
            ></InputNumber>
          </Form.Item>
          <Form.Item
            name={[
              "settings",
              "payments",
              "tipPercentagePresets",
              "percentage2",
            ]}
            label={"Tip Option 2"}
            labelCol={{ ...shortNumberFieldLayout.labelCol }}
            labelAlign="left"
          >
            <InputNumber
              controls={false}
              min={0}
              max={100}
              addonAfter={"%"}
            ></InputNumber>
          </Form.Item>
          <Form.Item
            name={[
              "settings",
              "payments",
              "tipPercentagePresets",
              "percentage3",
            ]}
            label={"Tip Option 3"}
            labelCol={{ ...shortNumberFieldWithSwitchLayout.labelCol }}
            labelAlign="left"
          >
            <InputNumber
              disabled={!showThirdTipOption}
              controls={false}
              min={0}
              max={100}
              addonAfter={"%"}
            ></InputNumber>

            <Form.Item
              name={[
                "settings",
                "payments",
                "tipPercentagePresets",
                "hidePercentage3",
              ]}
            >
              <Switch
                defaultChecked={
                  currentBusiness?.settings?.payments?.customTipButtonDisabled
                }
                checkedChildren="Show third tip option"
                unCheckedChildren="Hide third tip option"
                onChange={(value) => setShowThirdTipOption(!value)}
              />
            </Form.Item>
          </Form.Item>

          {/* 
          {!showThirdTipOption && (
            <Space
              align="end"
              style={{
                width: "100%",
                justifyContent: "end",
                marginBottom: "20px",
              }}
            >
              <Button
                icon={<PlusCircleOutlined />}
                type="text"
                onClick={() => setShowThirdTipOption(true)}
              >
                Add third tip option
              </Button>
            </Space>
          )} */}
        </>
      )}
      <Form.Item
        name={["settings", "payments", "enableSkipTags"]}
        label={"Enable Skip Tags"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={currentBusiness?.settings?.payments?.enableSkipTags}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "payments", "decimalFirstAmountEntry"]}
        label={"Enable Decimal First Amount Entry"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.payments?.decimalFirstAmountEntry
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Divider plain></Divider>
      <h3>Online Payments</h3>
      <Form.Item
        name={["settings", "payments", "isOnlinePaymentsFeatureEnabled"]}
        label={"Enable Online Payments"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.payments?.isOnlinePaymentsFeatureEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={onOnlinePaymentsEnablmentChange}
        />
      </Form.Item>
      {showOnlinePaymentsOptions && (
        <>
          <Form.Item
            name={["settings", "payments", "isPayAtTheTableFeatureEnabled"]}
            label={"Enable Pay At The Table"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.payments
                  ?.isPayAtTheTableFeatureEnabled
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
          <Form.Item
            name={["settings", "payments", "isPayByQRFeatureEnabled"]}
            label={"Enable Pay By QR"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={
                currentBusiness?.settings?.payments?.isPayByQRFeatureEnabled
              }
              checkedChildren="Yes"
              unCheckedChildren="No"
            />
          </Form.Item>
        </>
      )}
      <Divider plain></Divider>
      <h3>Receipt</h3>
      <Form.Item
        name={["settings", "receipt", "showAddress"]}
        label={"Show Address"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={currentBusiness?.settings?.receipt.showAddress}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "receipt", "hideVat"]}
        label={"Hide VAT"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={currentBusiness?.settings?.receipt?.hideVat}
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        labelCol={{ ...settingsFieldLayout.labelCol }}
        name={["settings", "receipt", "qrUrl"]}
        label={"QR Code URL"}
        labelAlign="left"
      >
        <Input max={200}></Input>
      </Form.Item>
      <Form.Item
        name={["settings", "receipt", "extraText"]}
        label={"Extra Text"}
        labelCol={{ ...settingsFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Input.TextArea></Input.TextArea>
      </Form.Item>
      <Divider plain></Divider>
      <h3>Insights</h3>
      {/* Outside Form.Item on purpose: it triggers a rebuild immediately and
          has no value to save with the form. */}
      <div style={{ marginBottom: 16 }}>
        <InsightsResyncButton
          businessId={currentBusiness?._id}
          businessName={currentBusiness?.businessName}
        />
      </div>
      <Form.Item
        name={["settings", "insights", "isInsightsFeatureEnabled"]}
        label={"Enable Insights Feature"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.insights?.isInsightsFeatureEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Divider plain></Divider>
      <h3>Reviews</h3>
      <Form.Item
        name={["settings", "reviews", "isReviewsFeatureEnabled"]}
        label={"Enable Reviews Feature"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.reviews?.isReviewsFeatureEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={onReviewsFeatureEnablmentChange}
        />
      </Form.Item>
      {showReviewsFeatureOptions && (
        <>
          <Form.Item
            name={["settings", "reviews", "enableReviews"]}
            label={"Enable Reviews"}
            labelCol={{ ...checkboxFieldLayout.labelCol }}
            labelAlign="left"
          >
            <Switch
              defaultChecked={currentBusiness?.settings?.reviews?.enableReviews}
              checkedChildren="Yes"
              unCheckedChildren="No"
              onChange={onReviewsEnablmentChange}
            />
          </Form.Item>
          {showReviewsOptions && (
            <Form.Item
              labelCol={{ ...settingsFieldLayout.labelCol }}
              name={["settings", "reviews", "googlePlaceId"]}
              label={"Google Place ID"}
              labelAlign="left"
            >
              <Input max={200}></Input>
            </Form.Item>
          )}
        </>
      )}
      <Divider plain></Divider>
      <h3>POS Integration</h3>
      <Form.Item
        name={["settings", "posIntegration", "isPosEmulatorEnabled"]}
        label={"Enable POS Emulator"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.posIntegration?.isPosEmulatorEnabled
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "payments", "blockDirectPayments"]}
        label={
          <span>
            Block Direct Payments{" "}
            <Tooltip title="When on, the terminal hides the amount keypad and shows the branded idle screen. Staff cannot take a standalone payment; the amount must come from the POS.">
              <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)" }} />
            </Tooltip>
          </span>
        }
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
        valuePropName="checked"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.payments?.blockDirectPayments
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Divider plain></Divider>
      <h3>
        Idle Screen{" "}
        <Tooltip title="The branded screen the terminal shows while waiting for the POS. Only visible when Block Direct Payments is on.">
          <InfoCircleOutlined style={{ color: "rgba(0,0,0,0.45)" }} />
        </Tooltip>
      </h3>
      <Form.Item
        name={["settings", "idleScreen", "logoUrl"]}
        label={"Logo URL"}
        labelCol={{ ...settingsFieldLayout.labelCol }}
        labelAlign="left"
      >
        <Input max={500}></Input>
      </Form.Item>
      <Form.Item
        name={["settings", "idleScreen", "textColor"]}
        label={"Text Color"}
        labelCol={{ ...settingsFieldLayout.labelCol }}
        labelAlign="left"
        rules={[
          {
            pattern: hexColorPattern,
            message: "Use a hex color such as #236B4D",
          },
        ]}
      >
        <Input placeholder="#236B4D" maxLength={7}></Input>
      </Form.Item>
      <Form.Item
        name={["settings", "idleScreen", "showTransactionListButton"]}
        label={"Show Transaction List Button"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
        valuePropName="checked"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.idleScreen?.showTransactionListButton
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
        />
      </Form.Item>
      <Form.Item
        name={["settings", "idleScreen", "showLogoutButton"]}
        label={"Show Logout Button"}
        labelCol={{ ...checkboxFieldLayout.labelCol }}
        labelAlign="left"
        valuePropName="checked"
      >
        <Switch
          defaultChecked={
            currentBusiness?.settings?.idleScreen?.showLogoutButton
          }
          checkedChildren="Yes"
          unCheckedChildren="No"
          onChange={setShowIdleScreenLogoutTexts}
        />
      </Form.Item>
      {showIdleScreenLogoutTexts &&
        idleScreenLogoutTexts.map(({ key, label, multiline }) => (
          <div key={key}>
            <Divider plain>{label}</Divider>
            {idleScreenLanguages.map(({ code, label: languageLabel }) => (
              <Form.Item
                key={code}
                name={["settings", "idleScreen", key, code]}
                label={languageLabel}
                labelCol={{ ...settingsFieldLayout.labelCol }}
                labelAlign="left"
              >
                {multiline ? (
                  <Input.TextArea autoSize={{ minRows: 1, maxRows: 3 }} />
                ) : (
                  <Input />
                )}
              </Form.Item>
            ))}
          </div>
        ))}
    </>
  );
}

/**
 * export interface IBusinessSettings {
  payments: {
    enableCashPayments: boolean;
    enableDiscounts: boolean;
    enableTips: boolean;
    optionalTipping: boolean; //default true
    tipPercentagePresets: {
      percentage1: number;
      percentage2: number;
      percentage3: number;
      percentage4: number;
    };
    enableSkipTags: boolean;
    decimalFirstAmountEntry: boolean;
  };
  endOfDay: {
    hour: number;
    minute: number;
  };
  receipt: {
    logoUrl: string;
    showAddress: boolean;
    extraText: string;
    qrUrl: string;
    hideVat: boolean;
  };
  reports: {
    showTips: boolean;
    sendList: string[];
    types: {
      endOfDaySalesOverviewReport: IReportSettings;
      weeklyPayoutReport: IReportSettings;
    };
  };
}
 */
